import { useEffect, useMemo, useRef, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import {
    useCreateScheduleTemplateMutation,
    useScheduleTemplate,
    useUpdateScheduleTemplateMutation,
} from "@api/scheduleTemplate.api";
import { useWorkspaceAccounts } from "@api/account.api";
import { MAX_SCHEDULE_TEMPLATE_NAME_LENGTH } from "@common/constants/domains/accounting/schedules";
import { buildDuplicateName } from "@common/helpers/duplicateName";
import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Drawer } from "@design-system/Drawer";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { Loader } from "@design-system/Loader/Loader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useEntities } from "@hooks/useEntities";
import { ConfirmDiscardScheduleChangesModal } from "./ConfirmDiscardScheduleChangesModal";
import { SchedulePreviewPane } from "./SchedulePreviewPane";
import { ScheduleForm } from "./ScheduleForm";
import {
    formToModel,
    getDefaultScheduleAccountEntityIds,
    getEmptyScheduleFormValues,
    makeScheduleAccountBlock,
    modelToForm,
    scheduleTemplateFormSchema,
    ScheduleTemplateFormValues,
} from "./scheduleFormHelpers";
import { changeSchedulePolicyKind } from "./schedulePolicyKindForm";
import { useScheduleHubScope } from "./ScheduleHubScopeProvider";
import styles from "./ScheduleDrawer.module.scss";

interface ScheduleDrawerProps {
    open: boolean;
    templateId: string | null;
    duplicateSourceId?: string | null;
    initialGroup?: string;
    initialEntityId?: number;
    onClose: () => void;
    onCreated?: (template: ScheduleTemplateResponse) => void;
    onAfterClose?: () => void;
}

export const ScheduleDrawer: React.FC<ScheduleDrawerProps> = ({
    open,
    templateId,
    duplicateSourceId = null,
    initialGroup,
    initialEntityId,
    onClose,
    onCreated,
    onAfterClose,
}) => {
    const { toast, error } = useToaster();
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const isDuplicating = templateId == null && duplicateSourceId != null;
    const loadedTemplateId = templateId ?? duplicateSourceId;
    const templateQuery = useScheduleTemplate(open ? loadedTemplateId : null);
    const workspaceAccounts = useWorkspaceAccounts();
    const businessEntities = useEntities({ onlyBusiness: true });
    const createMutation = useCreateScheduleTemplateMutation();
    const updateMutation = useUpdateScheduleTemplateMutation();
    const isSaving = createMutation.isPending || updateMutation.isPending;
    const defaultKind = useScheduleHubScope()?.defaultKind ?? null;

    const initialValues = useMemo(() => {
        if (templateQuery.data) {
            const formValues = modelToForm(
                templateQuery.data,
                workspaceAccounts.data ?? [],
            );
            if (isDuplicating) {
                return {
                    ...formValues,
                    name: buildDuplicateName({
                        sourceName: templateQuery.data.name,
                        maxLength: MAX_SCHEDULE_TEMPLATE_NAME_LENGTH,
                    }),
                };
            }
            return formValues;
        }
        const emptyValues = getEmptyScheduleFormValues({
            ...(initialGroup ? { group: initialGroup } : {}),
            accountBlocks: [
                makeScheduleAccountBlock({
                    entityIds: getDefaultScheduleAccountEntityIds({
                        initialEntityId,
                        businessEntityIds: businessEntities.map(
                            (entity) => entity.id,
                        ),
                    }),
                }),
            ],
        });
        return defaultKind
            ? {
                  ...emptyValues,
                  ...changeSchedulePolicyKind(emptyValues, defaultKind),
              }
            : emptyValues;
    }, [
        businessEntities,
        defaultKind,
        initialEntityId,
        initialGroup,
        isDuplicating,
        templateQuery.data,
        workspaceAccounts.data,
    ]);

    const formik = useFormik<ScheduleTemplateFormValues>({
        initialValues,
        validate: validateFormikWithZodSchema(scheduleTemplateFormSchema),
        onSubmit: submitHelper({
            handler: async (values) => {
                const result = formToModel(
                    values,
                    workspaceAccounts.data ?? [],
                );
                if (!result.ok) {
                    const [firstUnresolved] = result.unresolvedAccounts;
                    error(
                        `Account "${firstUnresolved.accountName}" is not available for every selected entity. Re-select the accounts.`,
                    );
                    return;
                }
                if (templateId) {
                    await updateMutation.mutateAsync({
                        id: templateId,
                        payload: result.body,
                    });
                    toast("Policy updated");
                } else {
                    const createdTemplate = await createMutation.mutateAsync(
                        result.body,
                    );
                    toast("Policy created");
                    onCreated?.(createdTemplate);
                }
                onClose();
            },
            failureHandler: async (submitError) => {
                error(submitError.message);
            },
        }),
    });

    const { resetForm, dirty } = formik;
    const isDirtyRef = useRef(dirty);
    isDirtyRef.current = dirty;
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (!open) {
            wasOpenRef.current = false;
            return;
        }
        const isOpening = !wasOpenRef.current;
        wasOpenRef.current = true;
        if (isOpening || !isDirtyRef.current) {
            resetForm({ values: initialValues });
        }
    }, [open, initialValues, resetForm]);

    const handleCloseAttempt = (cancel: () => void) => {
        if (formik.dirty) {
            cancel();
            setShowDiscardModal(true);
        }
    };

    const isEditing = templateId != null;
    const drawerTitle = isEditing ? "Edit policy" : "New policy";
    const submitLabel = isEditing ? "Confirm edit" : "Create policy";

    const isLoadingExisting =
        loadedTemplateId != null &&
        templateQuery.isLoading &&
        !templateQuery.data;
    const didFailLoadingExisting =
        loadedTemplateId != null &&
        templateQuery.isError &&
        !templateQuery.data;

    const renderDrawerBody = () => {
        if (isLoadingExisting) {
            return <Loader />;
        }
        if (didFailLoadingExisting) {
            return (
                <VStack gap={12} padding={24} align="start">
                    <p className={styles.helperText}>
                        Couldn't load this schedule. Please try again.
                    </p>
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={() => void templateQuery.refetch()}
                    >
                        Retry
                    </Button>
                </VStack>
            );
        }
        return (
            <FormikProvider value={formik}>
                <Form className={styles.drawerForm}>
                    <ResizablePanelGroup
                        orientation="horizontal"
                        className={styles.drawerBody}
                    >
                        <ResizablePanel
                            id="schedule-form-panel"
                            defaultSize="50%"
                            minSize="30%"
                        >
                            <ScheduleForm />
                        </ResizablePanel>
                        <ResizableHandle className={styles.resizeHandle} />
                        <ResizablePanel
                            id="schedule-preview-panel"
                            defaultSize="50%"
                            minSize="30%"
                        >
                            <SchedulePreviewPane values={formik.values} />
                        </ResizablePanel>
                    </ResizablePanelGroup>
                    <CommandIsland
                        sticky
                        alignEnd
                        className={commandIslandStyles.bulkActionsStickyFooter}
                    >
                        <ButtonWithLoader
                            size="md"
                            type="submit"
                            variant="primary"
                            loading={isSaving}
                            hotKey="mod+enter"
                            hotKeyOptions={{
                                enabled: !formik.isSubmitting,
                                description: submitLabel,
                            }}
                            hotKeyDisplayPosition="inline"
                        >
                            {submitLabel}
                        </ButtonWithLoader>
                    </CommandIsland>
                </Form>
            </FormikProvider>
        );
    };

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                onAnimationEnd={(isOpen) => {
                    if (!isOpen) {
                        onAfterClose?.();
                    }
                }}
                onCloseAttempt={handleCloseAttempt}
                direction="right"
                size="fullScreen"
                noPadding
                headerAddonLeft={
                    <span className={styles.drawerTitle}>{drawerTitle}</span>
                }
            >
                {renderDrawerBody()}
            </Drawer>
            <ConfirmDiscardScheduleChangesModal
                show={showDiscardModal}
                isSaving={isSaving}
                saveDisabled={Object.keys(formik.errors).length > 0}
                onSave={() => {
                    setShowDiscardModal(false);
                    void formik.submitForm();
                }}
                onDiscard={() => {
                    setShowDiscardModal(false);
                    onClose();
                }}
                onCancel={() => setShowDiscardModal(false)}
            />
        </>
    );
};
